import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";
import { OrderActionsCell } from "./OrderActionsCell";
import { getShortId } from "../../utils/getShortId";
import { PAYMENT_METHOD_LABELS, PAYMENT_PROVIDER_LABELS, ORDER_STATUS_LABELS } from "../../utils/orderConstants";
import { formatDate } from "../../utils/formatDate";
import { getStatusMeta, getPaidMeta } from "../../utils/statusMeta";
import { StatusChip } from "../common/StatusChip";

export const PAYMENT_CHANNEL_CONFIG = {
  pse_payment: {
    label: "PSE",
  },
  card_payment: {
    label: "Tarjeta",
  },
  bank_transfer: {
    label: "Transferencia Bancolombia",
  },
  cash_efecty: {
    label: "Efecty",
  },
  nequi_payment: {
    label: "Nequi",
  },
};

export const getOrderColumns = ({
  openPaymentProof
}) => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.display({
      id: "id",
      header: "ID",
      cell: (props) => {
        const id = props.row.original.id;
        return (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, color: "#5A6187", whiteSpace: "nowrap" }}>
            {getShortId(id)}...
          </span>
        );
      },
    }),

    columnHelper.display({
      id: "usuario",
      header: "Usuario",
      cell: (props) => {
        const user = props.row.original.user_id;
        return user ? (
          <>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{user.name}</div>
            <div style={{ color: "#8A91AE", fontSize: 11.5 }}>{user.email}</div>
          </>
        ) : (
          <span className="text-muted">Sin usuario</span>
        );
      },
    }),
    columnHelper.display({
      id: "destino",
      header: "Destino",
      cell: (props) => {
        const order = props.row.original;
        const s = order.address_id || order.shipping;
        if (!s) return <span className="text-muted">Recoger en tienda</span>;

        const country = s.country_id?.name || s.country;
        const department = s.province_id?.name || s.department;
        const city = s.city_id?.name || s.city;

        return (
          <div style={{ maxWidth: 200 }}>
            <div style={{ fontWeight: 600, fontSize: 12.5 }}>{country}</div>
            <div style={{ color: "#8A91AE", fontSize: 11.5 }}>
              {department}, {city}
              {s.postal_code && <> · CP {s.postal_code}</>}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("items", {
      header: "Cant. productos",
      cell: (info) => {
        const items = info.getValue() || [];
        const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        return <span style={{ fontWeight: 600 }}>{totalQuantity}</span>;
      },
    }),
    columnHelper.accessor("total", {
      header: "Total",
      cell: (info) => (
        <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          ${info.getValue().toLocaleString()}
        </span>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: "Estado",
      cell: (props) => {
        const status = props.row.original.status;
        const meta = getStatusMeta(status);
        return (
          <StatusChip tone={meta.tone} icon={meta.icon}>
            {ORDER_STATUS_LABELS[status] || status}
          </StatusChip>
        );
      },
    }),
    columnHelper.display({
      id: "is_paid",
      header: "Pagada",
      cell: (props) => {
        const meta = getPaidMeta(props.row.original);
        return <StatusChip tone={meta.tone}>{meta.label}</StatusChip>;
      },
    }),
   columnHelper.display({
      id: "payment_method",
      header: "Método de pago",
      cell: (props) => {
        const order = props.row.original;
        const method = order.payment_method;
        const provider = order.payment_provider;
        const channel = order.payment_channel;

        const methodLabel = PAYMENT_METHOD_LABELS[method] || "No especificado";

        const isMonnet = provider === "MONNET";
        const providerLabel =
          method === "PROVIDER" && provider
            ? ` (${PAYMENT_PROVIDER_LABELS[provider] || provider}${isMonnet ? "" : ""})`
            : "";

        const channelLabel =
          isMonnet && channel
            ? ` - ${PAYMENT_CHANNEL_CONFIG[channel]?.label || channel}`
            : "";

        const isStorePickupTransfer = method === "TRANSFER";

        return (
          <div>
            <div style={{ fontSize: 12.5 }}>
              {methodLabel}
              {providerLabel}
              {channelLabel}
            </div>

            {isStorePickupTransfer && (
              <button
                type="button"
                onClick={() => {
                  if (typeof openPaymentProof === "function") openPaymentProof(order);
                }}
                className="btn p-0 border-0"
                style={{ color: "#4B36F0", fontSize: 11.5, fontWeight: 600, textDecoration: "underline", marginTop: 2 }}
              >
                Ver comprobante
              </button>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      header: "Fecha creación",
      cell: (info) => {
        const dateValue = info.getValue();
        if (!dateValue) return "-";
        try {
          return formatDate(dateValue);
        } catch (error) {
          return format(new Date(dateValue), "dd/MM/yyyy HH:mm");
        }
      },
    }),

    columnHelper.display({
      id: "acciones",
      header: "Acciones",
      cell: (props) => <OrderActionsCell order={props.row.original} />,
    })
  ];
};
